import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface AuthModeResponse {
  AuthMode: 'credentials' | 'windows';
  ServerType: 'external' | 'internal' | 'unknown';
  RequiresLogin: boolean;
  WindowsAuthEnabled: boolean;
  Error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthConfigService {
  private baseUrl = environment.apiUrl;
  private authModeSubject = new BehaviorSubject<AuthModeResponse | null>(null);
  
  public authMode$ = this.authModeSubject.asObservable();
  
  public get currentAuthMode(): AuthModeResponse | null {
    return this.authModeSubject.value;
  }

  constructor(private http: HttpClient) {}

  detectAuthMode(): Observable<AuthModeResponse> {
    const url = `${this.baseUrl}account/auth-mode?_t=${Date.now()}`;
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    });

    return this.http.get<AuthModeResponse>(url, { headers })
      .pipe(
        tap(authMode => {
          this.authModeSubject.next(authMode);
        }),
        catchError(error => {
          const fallbackMode: AuthModeResponse = {
            AuthMode: 'credentials',
            ServerType: 'unknown',
            RequiresLogin: true,
            WindowsAuthEnabled: false,
            Error: 'Failed to detect auth mode'
          };
          
          this.authModeSubject.next(fallbackMode);
          return [fallbackMode];
        })
      );
  }

  isWindowsAuthMode(): boolean {
    const mode = this.currentAuthMode;
    return mode ? mode.AuthMode === 'windows' : false;
  }

  isCredentialsAuthMode(): boolean {
    const mode = this.currentAuthMode;
    return mode ? mode.AuthMode === 'credentials' : false; // Default to credentials
  }

  isWindowsAuthEnabled(): boolean {
    const mode = this.currentAuthMode;
    return mode ? mode.WindowsAuthEnabled : false;
  }

  requiresLogin(): boolean {
    const mode = this.currentAuthMode;
    return mode ? mode.RequiresLogin : true; // Default to requiring login
  }

  getServerType(): string {
    const mode = this.currentAuthMode;
    return mode ? mode.ServerType : 'unknown';
  }

  getAuthMode(): string {
    const mode = this.currentAuthMode;
    return mode ? mode.AuthMode : 'credentials';
  }

  isAuthModeDetected(): boolean {
    return this.currentAuthMode !== null;
  }

  initializeAuthMode(): Promise<AuthModeResponse> {
    return this.detectAuthMode().toPromise();
  }

  refreshAuthMode(): Promise<AuthModeResponse> {
    return this.detectAuthMode().toPromise();
  }
}